/**
 * Flag Detail — right pane: header + sub-tabs (Overview / Rules / Overrides / Audit).
 */

import { useState } from 'react';
import { Filter } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { useI18n } from '@/i18n';
import { parseBackendDateTime } from '@/lib/format/datetime';
import type { FlagResponse } from '@/types/featureFlags';
import { getExpiryInfo } from '../utils/stale';
import { getFlagDisplayState, FLAG_STATE_PILL } from '../utils/flagState';
import { OverviewPanel } from './OverviewPanel';
import { TargetingRulesEditor } from './TargetingRulesEditor';
import { UserOverridesPanel } from './UserOverridesPanel';
import { AuditLogPanel } from './AuditLogPanel';

interface FlagDetailProps {
  flag: FlagResponse;
  onDeleted?: () => void;
}

const LIFECYCLE_BADGE: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  draft: 'bg-yellow-100 text-yellow-800',
  archived: 'bg-gray-100 text-gray-600',
};

const FLAG_TYPE_BADGE: Record<string, string> = {
  deployment: 'bg-blue-100 text-blue-700',
  release: 'bg-purple-100 text-purple-700',
  isolation: 'bg-orange-100 text-orange-700',
  ops: 'bg-teal-100 text-teal-700',
};

export function FlagDetail({ flag, onDeleted }: FlagDetailProps) {
  const { t, language } = useI18n();
  const [tab, setTab] = useState('overview');

  const { state, rollout, hasRules } = getFlagDisplayState(flag);
  const stateLabel =
    state === 'partial' && rollout > 0 && rollout < 100
      ? t('featureFlags.enabledState.rollout', { pct: String(rollout) })
      : t(`featureFlags.enabledState.${state}`);

  const expiry = getExpiryInfo(flag.expires_at);
  const expiresLabel = flag.expires_at
    ? new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(parseBackendDateTime(flag.expires_at))
    : t('featureFlags.detail.noExpiry');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className={`text-xl font-semibold text-gray-900 ${flag.display_name ? '' : 'font-mono'}`}>
            {flag.display_name || flag.flag_key}
          </h2>
          <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${FLAG_STATE_PILL[state]}`}>
            {stateLabel}
          </span>
          <span className={`px-2 py-0.5 text-xs rounded ${FLAG_TYPE_BADGE[flag.flag_type] ?? 'bg-gray-100 text-gray-600'}`}>
            {flag.flag_type}
          </span>
          <span className={`px-2 py-0.5 text-xs rounded ${LIFECYCLE_BADGE[flag.lifecycle_state] ?? 'bg-gray-100 text-gray-600'}`}>
            {t(`featureFlags.lifecycleState.${flag.lifecycle_state}`)}
          </span>
          {hasRules && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-500">
              <Filter className="w-3 h-3" />
              {t('featureFlags.enabledState.hasRules')}
            </span>
          )}
        </div>
        {flag.display_name && (
          <p className="font-mono text-sm text-gray-400">{flag.flag_key}</p>
        )}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>{flag.owner || t('featureFlags.detail.noOwner')}</span>
          <span className="text-gray-300">·</span>
          <span
            className={
              expiry.status === 'expired'
                ? 'text-red-600'
                : expiry.status === 'soon'
                  ? 'text-yellow-700'
                  : ''
            }
          >
            {t('featureFlags.flagRow.expiresAt')}: {expiresLabel}
          </span>
        </div>
        {flag.description && <p className="text-sm text-gray-600">{flag.description}</p>}
      </div>

      {/* Sub-tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview">{t('featureFlags.detail.tabOverview')}</TabsTrigger>
          <TabsTrigger value="rules">{t('featureFlags.detail.tabRules')}</TabsTrigger>
          <TabsTrigger value="overrides">{t('featureFlags.detail.tabOverrides')}</TabsTrigger>
          <TabsTrigger value="audit">{t('featureFlags.detail.tabAudit')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewPanel flag={flag} onDeleted={onDeleted} />
        </TabsContent>
        <TabsContent value="rules">
          <TargetingRulesEditor flag={flag} />
        </TabsContent>
        <TabsContent value="overrides">
          <UserOverridesPanel flagKey={flag.flag_key} />
        </TabsContent>
        <TabsContent value="audit">
          <AuditLogPanel flagKey={flag.flag_key} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
